import React, { useMemo } from 'react';
import './PollCard.css';

/**
 * Parse a poll artifact's JSON content into a canonical shape:
 * { question, options:[string], votes:{ userId: optionIndex }, closed }.
 * Returns null when the content isn't a usable poll so the caller can fall back
 * to the raw text.
 */
export function parsePoll(content) {
  let data = content;
  if (typeof content === 'string') {
    try {
      data = JSON.parse(content);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const options = Array.isArray(data.options) ? data.options.map((o) => String(o)) : [];
  if (options.length < 2) return null;
  const votes =
    data.votes && typeof data.votes === 'object' && !Array.isArray(data.votes) ? data.votes : {};
  return {
    question: typeof data.question === 'string' ? data.question : '',
    options,
    votes,
    closed: data.closed === true,
  };
}

/**
 * Interactive poll rendered from a poll artifact. Unlike the self-contained
 * QuizCard, a poll's state is shared + persisted server-side: clicking an option
 * emits a `vote-poll` event and the live tally arrives back via `artifacts-updated`.
 * Clicking your current choice again clears your vote. Anyone can close the poll
 * to freeze the result (the server enforces who's allowed to).
 */
export default function PollCard({ content, title, artifactId, currentUserId, onVote, onClose }) {
  const poll = useMemo(() => parsePoll(content), [content]);

  const tally = useMemo(() => {
    if (!poll) return null;
    const counts = poll.options.map(() => 0);
    let total = 0;
    for (const idx of Object.values(poll.votes)) {
      const n = Number(idx);
      if (Number.isInteger(n) && n >= 0 && n < counts.length) {
        counts[n] += 1;
        total += 1;
      }
    }
    const max = Math.max(0, ...counts);
    return { counts, total, max };
  }, [poll]);

  if (!poll) {
    return <pre className="poll-raw">{String(content || '')}</pre>;
  }

  const heading = title || poll.question;
  const myVote = currentUserId ? poll.votes[currentUserId] : undefined;
  const canVote = !poll.closed && typeof onVote === 'function';

  const choose = (oi) => {
    if (!canVote || !artifactId) return;
    onVote(artifactId, oi);
  };

  return (
    <div className={`poll-card ${poll.closed ? 'closed' : ''}`}>
      {heading && <div className="poll-card-title">{heading}</div>}
      <ul className="poll-options">
        {poll.options.map((opt, oi) => {
          const count = tally.counts[oi];
          const pct = tally.total > 0 ? Math.round((count / tally.total) * 100) : 0;
          const isMine = myVote === oi;
          const isWinner = poll.closed && count > 0 && count === tally.max;
          return (
            <li key={oi}>
              <button
                type="button"
                className={`poll-option ${isMine ? 'mine' : ''} ${isWinner ? 'winner' : ''}`}
                onClick={() => choose(oi)}
                disabled={!canVote}
                aria-pressed={isMine}
                title={
                  canVote ? (isMine ? 'Click to remove your vote' : 'Vote for this option') : ''
                }
              >
                <span
                  className="poll-option-fill"
                  style={{ width: `${pct}%` }}
                  aria-hidden="true"
                />
                <span className="poll-option-body">
                  <span className="poll-option-label">
                    {isMine && (
                      <span className="poll-option-check" aria-hidden="true">
                        ✓
                      </span>
                    )}
                    {opt}
                  </span>
                  <span className="poll-option-count">
                    {pct}% · {count}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="poll-footer">
        <span className="poll-total">
          {tally.total} vote{tally.total === 1 ? '' : 's'}
          {poll.closed && ' · closed'}
        </span>
        {typeof onClose === 'function' && artifactId && (
          <button
            type="button"
            className="poll-close-btn"
            onClick={() => onClose(artifactId, !poll.closed)}
          >
            {poll.closed ? 'Reopen' : 'Close poll'}
          </button>
        )}
      </div>
    </div>
  );
}
